import React, { useState, useRef, useCallback, useEffect } from "react";
import apiHelper from "../../../../util/ApiHelper";
import { useLoading } from "../../../../util/LoadingProvider";
import {
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { CloudUpload } from "@material-ui/icons";
import { toast } from "react-toastify";

const VALID_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

const useStyles = makeStyles((theme) => ({
  section: {
    padding: theme.spacing(3),
  },
  listBox: {
    maxHeight: 400,
    overflowY: "auto",
    border: "1px solid #cccccc",
    borderRadius: 4,
  },
  uploadRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  fileName: {
    fontSize: 13,
    color: "#555",
  },
}));

export default function RefundFilmNamesPage({ disabled }) {
  const classes = useStyles();
  const { setLoading } = useLoading();
  const fileInputRef = useRef(null);

  const [filmList, setFilmList] = useState({ en: [], tc: [] });
  const [selectedFile, setSelectedFile] = useState(null);

  const fetchFilmList = useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiHelper.hkiff.getRefundFilmList();
      if (result && result.data) {
        setFilmList(result.data);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  useEffect(() => {
    fetchFilmList();
  }, [fetchFilmList]);

  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    if (file && !VALID_MIME_TYPES.includes(file.type)) {
      toast.error("Please upload a valid Excel file (.xlsx or .xls).");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setSelectedFile(file);
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.dark("Please select an Excel file first.");
      return;
    }
    try {
      setLoading(true);
      const result = await apiHelper.hkiff.importRefundFilmNames(selectedFile);
      if (result && result.data && result.data.code < 0) {
        return;
      }
      toast.dark("Film names imported successfully.");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await fetchFilmList();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box width="100%">
        <Typography variant="h4" gutterBottom>
          Refund &gt; Film Names
        </Typography>
      </Box>

      <Paper className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Import Film Names
        </Typography>
        <Box className={classes.uploadRow}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={handleFileChange}
            disabled={disabled}
          />
          <Button
            variant="outlined"
            color="primary"
            startIcon={<CloudUpload />}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={disabled}
          >
            Select File
          </Button>
          {selectedFile && (
            <Typography className={classes.fileName}>
              {selectedFile.name}
            </Typography>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={disabled || !selectedFile}
          >
            Import
          </Button>
        </Box>

        <Typography variant="h6" gutterBottom>
          Current Film List
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              English
            </Typography>
            <Paper variant="outlined" className={classes.listBox}>
              <List dense>
                {filmList.en && filmList.en.length > 0 ? (
                  filmList.en.map((name, i) => (
                    <ListItem key={i} divider>
                      <ListItemText primary={name} />
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="No films found"
                      primaryTypographyProps={{ color: "textSecondary" }}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Traditional Chinese
            </Typography>
            <Paper variant="outlined" className={classes.listBox}>
              <List dense>
                {filmList.tc && filmList.tc.length > 0 ? (
                  filmList.tc.map((name, i) => (
                    <ListItem key={i} divider>
                      <ListItemText primary={name} />
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="No films found"
                      primaryTypographyProps={{ color: "textSecondary" }}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
}
